import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { STAGES } from '@joker/domain'
import type { BucketSummary } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Loading, ErrorNote, Panel, ViewTitle } from '../ui'
import { CohortEditor, emptyDraft } from './CohortEditor'

/**
 * Every cohort: a defined group of people. What you do with one — see who is
 * in it, export it, watch it train, follow it through the funnel, send it to
 * Klaviyo — lives on its own page. Buckets were the same thing under another
 * name; they are cohorts now.
 */
export function CohortList({ onOpen, onCompare }: { onOpen: (id: string) => void; onCompare: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.buckets.list.queryOptions())
  const [creating, setCreating] = useState(false)
  const qc = useQueryClient()
  const reorder = useMutation(trpc.buckets.reorder.mutationOptions({ onSettled: () => qc.invalidateQueries() }))

  if (q.error) return <ErrorNote error={q.error} />

  const main = q.data?.filter((b) => !b.system) ?? []
  const system = q.data?.filter((b) => b.system) ?? []

  /* Moving one row saves the whole order, main list then System, so every
     cohort has a place and the list reads the same for everyone. */
  const move = (list: BucketSummary[], from: number, to: number) => {
    if (to < 0 || to >= list.length) return
    const next = [...list]
    const [row] = next.splice(from, 1)
    next.splice(to, 0, row!)
    const ids = (list === main ? [...next, ...system] : [...main, ...next]).map((b) => b.id)
    reorder.mutate({ ids })
  }

  return (
    <>
      <ViewTitle suit="heart">Cohorts</ViewTitle>
      <p className="view-sub">
        A cohort is a group of people defined by what is true of them: what they bought, where they
        signed up, how far they have trained. Open one to see who is in it, export it, compare its
        training with everybody else's, and later follow it through the funnel or send it to Klaviyo.
      </p>

      {creating && (
        <CohortEditor
          editing={null}
          initial={emptyDraft()}
          onDone={(id) => {
            setCreating(false)
            if (id) onOpen(id)
          }}
        />
      )}

      {/* The same frame as the Users list: a panel, a toolbar, then the table. */}
      <Panel>
        <div className="toolbar">
          <div className="controls">
            {!creating && (
              <button type="button" className="btn primary" onClick={() => setCreating(true)}>
                New cohort
              </button>
            )}
            <button type="button" className="btn ghost" onClick={onCompare}>
              Compare by channel, month, age…
            </button>
          </div>
          <div className="toolbar-end">
            <span className="count num">{q.data ? `${main.length} cohorts` : ''}</span>
          </div>
        </div>
        {reorder.error && <ErrorNote error={reorder.error} />}
        {!q.data ? (
          <Loading what="cohorts" />
        ) : (
          <CohortTable list={main} onOpen={onOpen} onMove={(from, to) => move(main, from, to)} busy={reorder.isPending} />
        )}
      </Panel>

      {system.length > 0 && (
        <Panel
          title="System"
          note="housekeeping: the only way to reach accounts hidden from every other screen. Can be edited, not archived"
        >
          <CohortTable list={system} onOpen={onOpen} onMove={(from, to) => move(system, from, to)} busy={reorder.isPending} />
        </Panel>
      )}
    </>
  )
}

function CohortTable({
  list,
  onOpen,
  onMove,
  busy,
}: {
  list: BucketSummary[]
  onOpen: (id: string) => void
  onMove: (from: number, to: number) => void
  busy: boolean
}) {
  /* Laid out like the Users list: the name in bold with what it is under it.
     A row is dragged and dropped anywhere to change the order; that never
     opens the cohort. */
  const [from, setFrom] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)
  /* The row dropped on is the target, read from the drop itself: a fast,
     long drag can land before the highlight has caught up. */
  const drop = (target: number) => {
    if (from != null && from !== target) onMove(from, target)
    setFrom(null)
    setOver(null)
  }
  return (
    <div className="scroll-x">
      <table className="data">
        <thead>
          <tr>
            <th aria-label="Order" className="cohort-move-h"></th>
            <th>Cohort</th>
            <th className="r">Users</th>
            <th>Opens as</th>
            <th>Goal</th>
          </tr>
        </thead>
        <tbody>
          {list.map((b, i) => (
            <tr
              key={b.id}
              tabIndex={0}
              draggable={!busy}
              onDragStart={(e) => {
                setFrom(i)
                e.dataTransfer.effectAllowed = 'move'
              }}
              onDragOver={(e) => {
                e.preventDefault()
                if (over !== i) setOver(i)
              }}
              onDrop={(e) => {
                e.preventDefault()
                drop(i)
              }}
              onDragEnd={() => {
                setFrom(null)
                setOver(null)
              }}
              className={[from === i ? 'cohort-dragging' : '', from != null && over === i && from !== i ? (over < from ? 'cohort-drop-above' : 'cohort-drop-below') : ''].join(' ')}
              onClick={() => onOpen(b.id)}
              onKeyDown={(e) => e.key === 'Enter' && onOpen(b.id)}
            >
              <td className="cohort-move" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <span className="cohort-handle" aria-hidden="true" title="Drag to move">⠿</span>
              </td>
              <td className="cohort-desc">
                <strong>{b.name}</strong>
                <br />
                <span className="muted sub-line">{b.description ?? b.ruleDescription}</span>
              </td>
              <td className="r num cohort-count">{b.memberCount.toLocaleString()}</td>
              <td>{b.audience === 'prospects' ? 'Leads' : 'Users'}</td>
              <td className="muted">{b.goalStage ? STAGES.find((s) => s.id === b.goalStage)?.label : (b.goalNote ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
